import {
  GapCloseType,
  PokemonType,
  SkillData,
  SkillEvent_AnchoredInvoke,
  SkillEvent_Cleanse,
  SkillEvent_CooldownSkip,
  SkillEvent_GapClose,
  SkillEvent_Global,
  SkillEvent_Heal,
  SkillEvent_MeleeHit,
  SkillEvent_ParticleEmission,
  SkillEvent_ProjectileCast,
  SkillEvent_Pull,
  SkillEvent_Shield,
  SkillEvent_StealBuff,
  SkillEvent_Tether,
  SkillEvent_Vfx,
  SkillEvent_Wait,
  SkillEventCheck,
  SkillMomentum,
  SkillRangeType,
  SkillTeamCondition,
  SkillType,
  StatusEffect,
  TeamConditionEffect,
} from "types/battle-engine/battle";
import { LINE_LOOP_PATTERN } from "../skill-patterns";
import { SQUARE_1_PATTERN } from "../skill-patterns/square-1";
import { SQUARE_2_PATTERN } from "../skill-patterns/square-2";
import { msToTicks } from "../utils/ms-to-ticks";
import {
  PokemonEventType,
  ReactiveTriggerConfig,
} from "types/battle-engine/events";

// new ai skills
export const PRISMATIC_CAGE: SkillData = {
  id: "prismatic-cage",
  name: "Prismatic Cage",
  description:
    "Launches a psychic orb that detonates into a shimmering cage around the target.",
  power: 10,
  pokemon_type: PokemonType.PSYCHIC,
  types: [SkillType.DAMAGE],
  cooldown_ticks: 350,
  mana_cost: 20,
  range: {
    type: SkillRangeType.POINT_TARGET,
    value: 5,
  },
  on_cast: [
    {
      __typename: "Vfx",
      duration_ticks: 35,
      target: {
        type: "self",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/self/charge-1-white.png",
      sprite_scale: 2.25,
      speed: 0.25,
      blocks_event_pipeline: true,
    } as SkillEvent_Vfx,
    {
      __typename: "ProjectileCast",
      sprite_path: "/assets/spells/projectile/psy-ball.png",
      duration_ticks: 35,
      damage: 12,
      speed: 35,
      sprite_scale: 1.4,
      target: {
        type: "enemy",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
      blocks_event_pipeline: true,
    } as SkillEvent_ProjectileCast,
  ],
  on_hit: [
    {
      __typename: "Vfx",
      duration_ticks: 35,
      target: {
        type: "enemy",
        id: 0,
      },
      status_effects: [
        {
          status: StatusEffect.SPEED_DOWN,
          duration_ticks: 150,
          intensity: 1,
        },
      ],
      starting_tick: 0,
      sprite_path: "/assets/spells/impact/fantasy-pink-1.png",
      sprite_scale: 1.5,
      speed: 0.35,
      blocks_event_pipeline: false,
    } as SkillEvent_Vfx,
    {
      __typename: "Vfx",
      duration_ticks: 40,
      target: {
        type: "enemy",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/vfx/stars_effect_000__40.png",
      sprite_scale: 1.25,
      speed: 0.6,
      anchor_to_target: true,
      y_offset: -60,
      blocks_event_pipeline: false,
    } as SkillEvent_Vfx,
    ...SQUARE_1_PATTERN().North.map(
      ([x, y]) =>
        ({
          __typename: "AnchoredInvoke",
          duration_ticks: 20,
          target: {
            type: "enemy",
            id: 0,
          },
          status_effects: [],
          starting_tick: 0,
          relative_x: x,
          relative_y: y,
          sprite_path: "/assets/spells/tile-fx/psychic-5.png",
          sprite_scale: 1.1,
          anim_loops: 1,
          anim_speed: 1,
          damage: 3,
          blocks_event_pipeline: false,
        }) as SkillEvent_AnchoredInvoke,
    ),
  ],
};

// ============================================================================
// TACTICAL SKILLS - 18 Strategic Skills (1 per type)
// Categories: Sacrifice/Recoil, Conditional/Execute, Cleanse/Purify, Buff Steal
// ============================================================================

// Category A: Sacrifice/Recoil Skills (5 skills)
// These skills deal self-damage or apply self-debuffs for powerful effects

export const FLARE_BLITZ: SkillData = {
  id: "flare-blitz",
  name: "Flare Blitz",
  description:
    "A reckless charge engulfed in flames. Deals massive damage but burns the user.",
  power: 25,
  pokemon_type: PokemonType.FIRE,
  types: [SkillType.DAMAGE],
  cooldown_ticks: msToTicks(6000),
  mana_cost: 20,
  range: {
    type: SkillRangeType.POINT_TARGET,
    value: 3,
  },
  on_hit: [
    {
      __typename: "Vfx",
      duration_ticks: 40,
      target: { type: "enemy", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path:
        "/assets/spells/impact/impfx2_fantasy_impact_A_large_red.png",
      sprite_scale: 2,
      speed: 0.35,
      blocks_event_pipeline: false,
    } as SkillEvent_Vfx,
  ],
  on_cast: [
    {
      __typename: "Vfx",
      duration_ticks: 25,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/vfx/charge__14.png",
      sprite_scale: 2,
      speed: 0.5,
      blocks_event_pipeline: true,
    } as SkillEvent_Vfx,
    {
      __typename: "ProjectileCast",
      sprite_path: "/assets/spells/projectile/fire-ball.png",
      duration_ticks: 40,
      damage: 25,
      speed: 50,
      sprite_scale: 1.5,
      target: { type: "enemy", id: 0 },
      status_effects: [
        { status: StatusEffect.BURN, duration_ticks: 999999, intensity: 2 },
      ],
      starting_tick: 0,
      blocks_event_pipeline: true,
      self_damage: 8,
      self_status_effects: [
        { status: StatusEffect.BURN, duration_ticks: 999999, intensity: 1 },
      ],
    } as SkillEvent_ProjectileCast,
  ],
};

export const CLOSE_COMBAT: SkillData = {
  id: "close-combat",
  name: "Close Combat",
  description:
    "An all-out attack that lowers the user's defenses but deals heavy damage.",
  power: 30,
  pokemon_type: PokemonType.FIGHTING,
  types: [SkillType.DAMAGE],
  cooldown_ticks: msToTicks(5000),
  mana_cost: 15,
  range: {
    type: SkillRangeType.POINT_TARGET,
    value: 1,
  },
  on_hit: [
    {
      __typename: "Vfx",
      duration_ticks: 20,
      target: { type: "enemy", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/impact/melee-fighting-1.png",
      sprite_scale: 1.2,
      blocks_event_pipeline: false,
    } as SkillEvent_Vfx,
  ],
  on_cast: [
    {
      __typename: "Vfx",
      duration_ticks: 15,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/vfx/whiteredbuff__21.png",
      sprite_scale: 1.5,
      speed: 0.6,
      blocks_event_pipeline: false,
    } as SkillEvent_Vfx,
    {
      __typename: "MeleeHit",
      duration_ticks: 35,
      target: { type: "enemy", id: 0 },
      status_effects: [],
      starting_tick: 0,
      damage: 30,
      blocks_event_pipeline: true,
      self_status_effects: [
        {
          status: StatusEffect.DEFENSE_DOWN,
          duration_ticks: 300,
          intensity: 2,
        },
      ],
    } as SkillEvent_MeleeHit,
  ],
};

// NEEDS TESTING AND CONFIRMATION => AI GENERATED
export const CROSS_CHOP: SkillData = {
  id: "cross-chop",
  name: "Cross Chop",
  description: "A double-chopping attack with high critical hit ratio.",
  power: 25,
  pokemon_type: PokemonType.FIGHTING,
  types: [SkillType.DAMAGE],
  cooldown_ticks: msToTicks(4000),
  mana_cost: 12,
  range: {
    type: SkillRangeType.POINT_TARGET,
    value: 1,
  },
  team_conditions: [
    {
      condition: {
        type: "min_ally_type_count",
        pokemon_type: PokemonType.FIGHTING,
        value: 1,
      },
      effect: {
        stat: "damage_mult",
        value: 1.4,
      } as TeamConditionEffect,
    },
  ] as SkillData["team_conditions"],
  on_hit: [
    {
      __typename: "Vfx",
      duration_ticks: 25,
      target: { type: "enemy", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/impact/melee-red-3.png",
      sprite_scale: 1.5,
      blocks_event_pipeline: false,
    } as SkillEvent_Vfx,
  ],
  on_cast: [
    {
      __typename: "Vfx",
      duration_ticks: 20,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/melee-cast/slash-red.png",
      sprite_scale: 1.2,
      speed: 1.5,
      loops: 2,
      anchor_to_target: false,
      blocks_event_pipeline: false,
    } as SkillEvent_Vfx,
    {
      __typename: "MeleeHit",
      duration_ticks: 40,
      target: { type: "enemy", id: 0 },
      status_effects: [],
      starting_tick: 10,
      damage: 25,
      blocks_event_pipeline: true,
      critical_hit_multiplier: 2.0,
    } as SkillEvent_MeleeHit,
  ],
};

export const SOUL_SACRIFICE: SkillData = {
  id: "soul-sacrifice",
  name: "Soul Sacrifice",
  description:
    "Sacrifices a portion of HP to curse all enemies with burning and poison.",
  power: 0,
  pokemon_type: PokemonType.GHOST,
  types: [SkillType.DEBUFF],
  cooldown_ticks: msToTicks(12000),
  mana_cost: 25,
  range: {
    type: SkillRangeType.ALL_ENEMIES,
    value: 0,
  },
  on_hit: [],
  on_cast: [
    {
      __typename: "Vfx",
      duration_ticks: 40,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/vfx/fear_000__19.png",
      sprite_scale: 1.5,
      speed: 0.3,
      blocks_event_pipeline: true,
    } as SkillEvent_Vfx,
    {
      __typename: "Global",
      duration_ticks: 35,
      target: { type: "enemy", id: 0 },
      status_effects: [
        { status: StatusEffect.BURN, duration_ticks: 999999, intensity: 2 },
        { status: StatusEffect.POISON, duration_ticks: 300, intensity: 2 },
      ],
      starting_tick: 0,
      vfx: {
        sprite_path: "/assets/spells/tile-fx/dark-3.png",
        sprite_scale: 1.5,
        speed: 0.4,
        anchor_to_target: true,
      },
      blocks_event_pipeline: true,
      self_damage: 25,
    } as SkillEvent_Global,
  ],
};

export const DARK_BARGAIN: SkillData = {
  id: "dark-bargain",
  name: "Dark Bargain",
  description:
    "Sacrifices HP to gain a massive attack boost. A deal with darkness.",
  power: 0,
  pokemon_type: PokemonType.DARK,
  types: [SkillType.BUFF],
  cooldown_ticks: msToTicks(10000),
  mana_cost: 15,
  range: {
    type: SkillRangeType.SELF,
    value: 0,
  },
  on_hit: [],
  on_cast: [
    {
      __typename: "Vfx",
      duration_ticks: 50,
      target: { type: "self", id: 0 },
      status_effects: [
        { status: StatusEffect.ATTACK_UP, duration_ticks: 500, intensity: 3 },
      ],
      starting_tick: 0,
      sprite_path: "/assets/spells/tile-fx/dark-2.png",
      sprite_scale: 2,
      speed: 0.25,
      blocks_event_pipeline: true,
    } as SkillEvent_Vfx,
    {
      __typename: "Global",
      duration_ticks: 1,
      target: { type: "ally", id: 0 },
      status_effects: [],
      starting_tick: 0,
      blocks_event_pipeline: false,
      self_damage: 20,
    } as SkillEvent_Global,
  ],
};

export const TOXIC_OVERLOAD: SkillData = {
  id: "toxic-overload",
  name: "Toxic Overload",
  description: "Poisons self to unleash devastating toxins on all enemies.",
  power: 0,
  pokemon_type: PokemonType.POISON,
  types: [SkillType.DEBUFF],
  cooldown_ticks: msToTicks(10000),
  mana_cost: 20,
  range: {
    type: SkillRangeType.ALL_ENEMIES,
    value: 0,
  },
  event_checks: [
    {
      conditions: [
        {
          type: "TARGET_HAS_STATUS",
          status: StatusEffect.POISON,
        },
      ],
      effects: [
        {
          type: "HEAL_ALL_ALLIES_FIXED",
          value: 10,
        },
      ],
    },
  ] as SkillData["event_checks"],
  on_hit: [],
  on_cast: [
    {
      __typename: "Vfx",
      duration_ticks: 30,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/vfx/poisontrap__13.png",
      sprite_scale: 1.5,
      speed: 0.4,
      blocks_event_pipeline: true,
    } as SkillEvent_Vfx,
    {
      __typename: "Global",
      duration_ticks: 35,
      target: { type: "enemy", id: 0 },
      status_effects: [
        { status: StatusEffect.POISON, duration_ticks: 400, intensity: 3 },
      ],
      starting_tick: 0,
      vfx: {
        sprite_path: "/assets/spells/tile-fx/poison-4.png",
        sprite_scale: 1,
        speed: 0.5,
        anchor_to_target: true,
      },
      blocks_event_pipeline: true,
    } as SkillEvent_Global,
  ],
};

// Category B: Conditional/Execute Skills (5 skills)
// These skills deal bonus damage based on battle conditions

export const OUTRAGE: SkillData = {
  id: "outrage",
  name: "Outrage",
  description:
    "Deals double damage when the user is below 40% HP. Fueled by rage.",
  power: 18,
  pokemon_type: PokemonType.DRAGON,
  types: [SkillType.DAMAGE],
  cooldown_ticks: msToTicks(7000),
  mana_cost: 15,
  range: {
    type: SkillRangeType.POINT_TARGET,
    value: 3,
  },
  on_hit: [],
  on_cast: [
    {
      __typename: "Vfx",
      duration_ticks: 15,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/vfx/spheresbuff__21.png",
      sprite_scale: 1.75,
      speed: 0.65,
      blocks_event_pipeline: true,
    } as SkillEvent_Vfx,
    {
      __typename: "GapClose",
      duration_ticks: 15,
      target: { type: "enemy", id: 0 },
      damage: 10,
      range: 3,
      gap_close_type: GapCloseType.TARGET_NEAREST,
      status_effects: [],
      starting_tick: 0,
      blocks_event_pipeline: true,
    } as SkillEvent_GapClose,
    ...new Array(4)
      .fill(0)
      .map((_, index) => [
        {
          __typename: "MeleeHit",
          duration_ticks: 15,
          target: { type: "enemy", id: 0 },
          status_effects: [],
          starting_tick: 0,
          damage: 8,
          blocks_event_pipeline: true,
        } as SkillEvent_MeleeHit,

        {
          __typename: "Vfx",
          duration_ticks: 10,
          target: { type: "enemy", id: 0 },
          status_effects: [],
          starting_tick: 0,
          sprite_path: "/assets/spells/impact/melee-dragon-3.png",
          sprite_scale: 1,
          speed: 0.5,
          blocks_event_pipeline: false,
        } as SkillEvent_Vfx,
      ])
      .flat(),
    {
      __typename: "Wait",
      duration_ticks: msToTicks(2000),
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      blocks_event_pipeline: true,
    } as SkillEvent_Wait,
  ],
  event_checks: [
    {
      conditions: [
        {
          type: "CASTER_HP_LT",
          value: 40,
        },
      ],
      effects: [
        {
          type: "DAMAGE_MULTIPLIER",
          value: 1.4,
        },
      ],
    },
  ] as SkillData["event_checks"],
};

// Expanding wave pattern for Iron Fortress (like Shadow Fiend's ult)
export const METAL_RUSH_WAVE_PATTERN: [number, number][] = [
  // Ring 1 (closest)
  [0, -1],
  [0, 1],
  [-1, 0],
  [1, 0],
  // Ring 2
  [0, -2],
  [0, 2],
  [-2, 0],
  [2, 0],
  [-1, -1],
  [-1, 1],
  [1, -1],
  [1, 1],
  // Ring 3 (furthest)
  [0, -3],
  [0, 3],
  [-3, 0],
  [3, 0],
  [-1, -2],
  [-1, 2],
  [1, -2],
  [1, 2],
  [-2, -1],
  [-2, 1],
  [2, -1],
  [2, 1],
];

export const METAL_RUSH: SkillData = {
  id: "metal-rush",
  name: "Metal Rush",
  description: "Releases an expanding wave of steel. ",
  power: 0,
  pokemon_type: PokemonType.STEEL,
  types: [SkillType.DAMAGE],
  cooldown_ticks: msToTicks(6000),
  mana_cost: 18,
  range: {
    type: SkillRangeType.ALL_ENEMIES,
    value: 3,
  },
  charging: {
    is_required: true,
    align_to: "south",
    anim_on_end: {
      name: "rotate",
      dir: "south",
    },
    duration_ticks: 100,
  } as const,
  on_hit: [],
  on_cast: [
    {
      __typename: "Vfx",
      duration_ticks: 30,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/vfx/steelsaw__17.png",
      sprite_scale: 2,
      speed: 0.4,
      blocks_event_pipeline: true,
    } as SkillEvent_Vfx,
    // Expanding wave using AnchoredInvoke - Ring 1 (immediate)
    ...(
      [
        [0, -1],
        [0, 1],
        [-1, 0],
        [1, 0],
      ] as [number, number][]
    ).map(
      ([x, y]) =>
        ({
          __typename: "AnchoredInvoke",
          duration_ticks: 15,
          target: { type: "self", id: 0 },
          status_effects: [],
          starting_tick: 0,
          relative_x: x,
          relative_y: y,
          sprite_path: "/assets/spells/tile-fx/pj4_sawblade_small_gray.png",
          sprite_scale_x: 2.7,
          sprite_scale_y: 1.8,
          anim_speed: 1.5,
          damage: 5,
          blocks_event_pipeline: false,
        }) as SkillEvent_AnchoredInvoke,
    ),
    {
      __typename: "Wait",
      duration_ticks: 10,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      blocks_event_pipeline: true,
    } as SkillEvent_Wait,
    // Ring 2 (delayed by wait)
    ...(
      [
        [0, -2],
        [0, 2],
        [-2, 0],
        [2, 0],
        [-1, -1],
        [-1, 1],
        [1, -1],
        [1, 1],
      ] as [number, number][]
    ).map(
      ([x, y]) =>
        ({
          __typename: "AnchoredInvoke",
          duration_ticks: 15,
          target: { type: "self", id: 0 },
          status_effects: [],
          starting_tick: 0,
          relative_x: x,
          relative_y: y,
          sprite_path: "/assets/spells/tile-fx/pj4_sawblade_small_gray.png",
          sprite_scale_x: 2.7,
          sprite_scale_y: 1.8,
          anim_speed: 1.5,
          damage: 5,
          blocks_event_pipeline: false,
        }) as SkillEvent_AnchoredInvoke,
    ),
    {
      __typename: "Wait",
      duration_ticks: 10,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      blocks_event_pipeline: true,
    } as SkillEvent_Wait,
    // Ring 3 (most delayed by second wait)
    ...(
      [
        [0, -3],
        [0, 3],
        [-3, 0],
        [3, 0],
        [-1, -2],
        [-1, 2],
        [1, -2],
        [1, 2],
        [-2, -1],
        [-2, 1],
        [2, -1],
        [2, 1],
      ] as [number, number][]
    ).map(
      ([x, y]) =>
        ({
          __typename: "AnchoredInvoke",
          duration_ticks: 15,
          target: { type: "self", id: 0 },
          status_effects: [],
          starting_tick: 0,
          relative_x: x,
          relative_y: y,
          sprite_path: "/assets/spells/tile-fx/pj4_sawblade_small_gray.png",
          sprite_scale_x: 2.7,
          sprite_scale_y: 1.8,
          anim_speed: 1.5,
          damage: 5,
          blocks_event_pipeline: false,
        }) as SkillEvent_AnchoredInvoke,
    ),
    {
      __typename: "Wait",
      duration_ticks: 15,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      blocks_event_pipeline: true,
    } as SkillEvent_Wait,
  ],
};

export const LIQUIDATION: SkillData = {
  id: "liquidation",
  name: "Liquidation",
  description: "If this Pokemon has SPEED UP, deals 40% bonus damage.",
  event_checks: [
    {
      __typename: "SkillEventCheck",
      conditions: [
        {
          type: "CASTER_HAS_STATUS",
          status: StatusEffect.SPEED_UP,
        },
      ],
      effects: [
        {
          type: "DAMAGE_MULTIPLIER",
          value: 1.4,
        },
      ],
    } as SkillEventCheck,
  ],
  power: 18,
  pokemon_type: PokemonType.WATER,
  types: [SkillType.DAMAGE],
  cooldown_ticks: msToTicks(6500),
  mana_cost: 12,
  range: {
    type: SkillRangeType.POINT_TARGET,
    value: 1,
  },
  on_hit: [],
  on_cast: [
    {
      __typename: "ProjectileCast",
      sprite_path: "/assets/spells/projectile/water-2.png",
      duration_ticks: 40,
      damage: 18,
      speed: 45,
      sprite_scale: 1.3,
      target: { type: "enemy", id: 0 },
      status_effects: [],
      starting_tick: 0,
      blocks_event_pipeline: true,
    } as SkillEvent_ProjectileCast,
    {
      __typename: "Global",
      duration_ticks: 15,
      target: { type: "enemy", id: 0 },
      damage: 9,
      status_effects: [],
      starting_tick: 0,
      vfx: {
        sprite_path: "/assets/spells/tile-fx/water-1.png",
        sprite_scale: 1,
        speed: 0.6,
        anchor_to_target: true,
      },
      blocks_event_pipeline: false,
      event_check: {
        __typename: "TargetHasStatus",
        status_effect: StatusEffect.SPEED_DOWN,
      },
    } as SkillEvent_Global,
  ],
};

export const VOLT_CRASH: SkillData = {
  id: "volt-crash",
  name: "Volt Crash",
  description: "Deals double damage to stunned targets. Amplifies the shock.",
  power: 13,
  pokemon_type: PokemonType.ELECTRIC,
  types: [SkillType.DAMAGE],
  cooldown_ticks: msToTicks(5500),
  mana_cost: 15,
  range: {
    type: SkillRangeType.POINT_TARGET,
    value: 4,
  },
  event_checks: [
    {
      __typename: "SkillEventCheck",
      conditions: [
        {
          type: "TARGET_HAS_STATUS",
          status: StatusEffect.STUN,
        },
      ],
      effects: [
        {
          type: "DAMAGE_MULTIPLIER",
          value: 2,
        },
      ],
    } as SkillEventCheck,
  ],
  on_hit: [],
  on_cast: [
    {
      __typename: "ProjectileCast",
      sprite_path: "/assets/spells/projectile/thunderbolt.png",
      duration_ticks: 35,
      damage: 12,
      speed: 50,
      sprite_scale: 1.2,
      target: { type: "enemy", id: 0 },
      status_effects: [],
      starting_tick: 0,
      blocks_event_pipeline: true,
    } as SkillEvent_ProjectileCast,
    {
      __typename: "Global",
      duration_ticks: 20,
      target: { type: "enemy", id: 0 },
      damage: 13,
      status_effects: [],
      starting_tick: 0,
      vfx: {
        sprite_path: "/assets/spells/tile-fx/electric-1.png",
        sprite_scale: 1,
        speed: 0.7,
        anchor_to_target: true,
      },
      blocks_event_pipeline: false,
      event_check: {
        __typename: "TargetHasStatus",
        status_effect: StatusEffect.STUN,
      },
    } as SkillEvent_Global,
  ],
};

export const SHEER_COLD: SkillData = {
  id: "sheer-cold",
  name: "Sheer Cold",
  description:
    "A chilling attack that deals extra damage if the user's HP is below 50%.",
  power: 10,
  pokemon_type: PokemonType.ICE,
  types: [SkillType.DAMAGE],
  cooldown_ticks: msToTicks(9000),
  mana_cost: 18,
  range: {
    type: SkillRangeType.ANCHORED_INVOKE,
    value: 3,
  },
  event_checks: [
    {
      __typename: "SkillEventCheck",
      conditions: [
        {
          type: "CASTER_HP_LT",
          value: 0.5,
        },
      ],

      effects: [
        {
          type: "DAMAGE_MULTIPLIER",
          value: 1.5,
        },
      ],
    } as SkillEventCheck,
  ],
  charging: {
    is_required: true,
    align_to: "south",
    anim_on_end: {
      name: "rotate",
      dir: "south",
    },
    duration_ticks: 100,
  } as const,
  on_hit: [],
  on_cast: [
    // Initial ice VFX on caster
    {
      __typename: "Vfx",
      duration_ticks: 30,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/tile-fx/ice-2.png",
      sprite_scale: 1.2,
      speed: 0.4,
      blocks_event_pipeline: true,
    } as SkillEvent_Vfx,
    // Expanding wave using AnchoredInvoke - Ring 1 (immediate)
    ...(
      [
        [0, -1],
        [0, 1],
        [-1, 0],
        [1, 0],
      ] as [number, number][]
    ).map(
      ([x, y]) =>
        ({
          __typename: "AnchoredInvoke",
          duration_ticks: 15,
          target: { type: "self", id: 0 },
          status_effects: [],
          starting_tick: 0,
          relative_x: x,
          relative_y: y,
          sprite_path: "/assets/spells/tile-fx/ice-3.png",
          sprite_scale: 1.2,
          anim_speed: 1.5,
          damage: 4,
          blocks_event_pipeline: false,
        }) as SkillEvent_AnchoredInvoke,
    ),
    {
      __typename: "Wait",
      duration_ticks: 10,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      blocks_event_pipeline: true,
    } as SkillEvent_Wait,
    // Ring 2 (delayed by wait)
    ...(
      [
        [0, -2],
        [0, 2],
        [-2, 0],
        [2, 0],
        [-1, -1],
        [-1, 1],
        [1, -1],
        [1, 1],
      ] as [number, number][]
    ).map(
      ([x, y]) =>
        ({
          __typename: "AnchoredInvoke",
          duration_ticks: 15,
          target: { type: "self", id: 0 },
          status_effects: [],
          starting_tick: 0,
          relative_x: x,
          relative_y: y,
          sprite_path: "/assets/spells/tile-fx/ice-3.png",
          sprite_scale: 1.2,
          anim_speed: 1.5,
          damage: 4,
          blocks_event_pipeline: false,
        }) as SkillEvent_AnchoredInvoke,
    ),
    {
      __typename: "Wait",
      duration_ticks: 10,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      blocks_event_pipeline: true,
    } as SkillEvent_Wait,
    // Ring 3 (most delayed by second wait)
    ...(
      [
        [0, -3],
        [0, 3],
        [-3, 0],
        [3, 0],
        [-1, -2],
        [-1, 2],
        [1, -2],
        [1, 2],
        [-2, -1],
        [-2, 1],
        [2, -1],
        [2, 1],
      ] as [number, number][]
    ).map(
      ([x, y]) =>
        ({
          __typename: "AnchoredInvoke",
          duration_ticks: 15,
          target: { type: "self", id: 0 },
          status_effects: [],
          starting_tick: 0,
          relative_x: x,
          relative_y: y,
          sprite_path: "/assets/spells/tile-fx/ice-3.png",
          sprite_scale: 1.2,
          anim_speed: 1.5,
          damage: 4,
          blocks_event_pipeline: false,
        }) as SkillEvent_AnchoredInvoke,
    ),
    {
      __typename: "Wait",
      duration_ticks: 15,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      blocks_event_pipeline: true,
    } as SkillEvent_Wait,
  ],
};

// Category C: Cleanse/Purify Skills (4 skills)
// These skills remove debuffs from allies or self

export const HEAL_BELL: SkillData = {
  id: "heal-bell",
  name: "Heal Bell",
  description:
    "A soothing bell that heals and cleanses all debuffs from all allies.",
  power: 0,
  pokemon_type: PokemonType.FAIRY,
  types: [SkillType.BUFF],
  cooldown_ticks: msToTicks(15000),
  mana_cost: 25,
  range: {
    type: SkillRangeType.ALL_ALLIES,
    value: 0,
  },
  on_hit: [],
  on_cast: [
    {
      __typename: "Vfx",
      duration_ticks: 30,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/vfx/bell_ring_and_sounds_008__16.png",
      sprite_scale: 1.5,
      speed: 0.4,
      blocks_event_pipeline: true,
    } as SkillEvent_Vfx,
    {
      __typename: "Global",
      duration_ticks: 35,
      target: { type: "ally", id: 0 },
      status_effects: [],
      starting_tick: 0,
      vfx: {
        sprite_path: "/assets/spells/vfx/bell_ring_and_sounds_001__18.png",
        sprite_scale: 1,
        speed: 0.5,
        anchor_to_target: true,
      },
      healing: 10,
      blocks_event_pipeline: true,
    } as SkillEvent_Global,
    {
      __typename: "Cleanse",
      duration_ticks: 1,
      target: { type: "ally", id: 0 },
      status_effects: [],
      starting_tick: 0,
      cleanse_count: 0, // 0 = all debuffs
      blocks_event_pipeline: false,
    } as SkillEvent_Cleanse,
  ],
};

export const SYNTHESIS: SkillData = {
  id: "synthesis",
  name: "Synthesis",
  description:
    "Heals an ally and cleanses all debuffs. Extra healing on WATER-type allies.",
  power: 0,
  pokemon_type: PokemonType.GRASS,
  types: [SkillType.HEAL],
  cooldown_ticks: msToTicks(5000),
  mana_cost: 15,
  range: {
    type: SkillRangeType.POINT_TARGET,
    value: 5,
  },
  on_hit: [],
  on_cast: [
    {
      __typename: "Vfx",
      duration_ticks: 20,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/vfx/grassbuff__26.png",
      sprite_scale: 1.5,
      speed: 0.4,
      blocks_event_pipeline: false,
    } as SkillEvent_Vfx,
    {
      __typename: "Vfx",
      duration_ticks: 30,
      target: { type: "ally", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/self/healing-2-green.png",
      sprite_scale: 1.5,
      speed: 0.25,
      blocks_event_pipeline: true,
    } as SkillEvent_Vfx,
    {
      __typename: "Heal",
      duration_ticks: 1,
      target: { type: "ally", id: 0 },
      status_effects: [],
      starting_tick: 0,
      heal_amount: 25,
      blocks_event_pipeline: false,
    } as SkillEvent_Heal,
    {
      __typename: "Cleanse",
      duration_ticks: 1,
      target: { type: "ally", id: 0 },
      status_effects: [],
      starting_tick: 0,
      cleanse_count: 0,
      blocks_event_pipeline: false,
    } as SkillEvent_Cleanse,
  ],
  event_checks: [
    {
      conditions: [
        {
          type: "TARGET_IS_TYPE",
          pokemon_type: PokemonType.WATER,
        },
      ],
      effects: [
        {
          type: "HEAL_FIXED",
          value: 10,
        },
      ],
    },
  ],
};

export const CALM_MIND: SkillData = {
  id: "calm-mind",
  name: "Calm Mind",
  description: "Clears the mind, removing all debuffs and boosting attack +3.",
  power: 0,
  pokemon_type: PokemonType.PSYCHIC,
  types: [SkillType.BUFF],
  cooldown_ticks: msToTicks(5000),
  mana_cost: 20,
  range: {
    type: SkillRangeType.SELF,
    value: 9999,
  },
  on_hit: [],
  on_cast: [
    {
      __typename: "Vfx",
      duration_ticks: 25,
      target: { type: "self", id: 0 },
      status_effects: [
        {
          status: StatusEffect.ATTACK_UP,
          duration_ticks: msToTicks(8000),
          intensity: 3,
        },
      ],
      starting_tick: 0,
      sprite_path: "/assets/spells/vfx/spellcast__39.png",
      sprite_scale: 1.5,
      speed: 0.4,
      blocks_event_pipeline: true,
    } as SkillEvent_Vfx,
    {
      __typename: "Cleanse",
      duration_ticks: 1,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      cleanse_count: 0, // 0 = all debuffs
      blocks_event_pipeline: false,
    } as SkillEvent_Cleanse,
  ],
};

export const PURIFICATION: SkillData = {
  id: "purification",
  name: "Purification",
  description: "Purifies the body, removing all debuffs and healing.",
  power: 0,
  pokemon_type: PokemonType.NORMAL,
  types: [SkillType.HEAL],
  cooldown_ticks: msToTicks(6000),
  mana_cost: 12,
  range: {
    type: SkillRangeType.SELF,
    value: 0,
  },
  on_hit: [],
  on_cast: [
    {
      __typename: "Vfx",
      duration_ticks: 35,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/self/healing-2-white.png",
      sprite_scale: 1.5,
      speed: 0.3,
      blocks_event_pipeline: true,
    } as SkillEvent_Vfx,
    {
      __typename: "Heal",
      duration_ticks: 1,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      heal_amount: 15,
      blocks_event_pipeline: false,
    } as SkillEvent_Heal,
    {
      __typename: "Cleanse",
      duration_ticks: 1,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      cleanse_count: 0, // 0 = all debuffs
      blocks_event_pipeline: false,
    } as SkillEvent_Cleanse,
  ],
};

// Category D: Buff Steal Skills (4 skills)
// These skills steal buffs from enemies

export const SPECTRAL_SNATCH: SkillData = {
  id: "spectral-snatch",
  name: "Spectral Snatch",
  description: "Steals one random buff from the target with ghostly hands.",
  power: 10,
  pokemon_type: PokemonType.GHOST,
  types: [SkillType.DAMAGE],
  cooldown_ticks: msToTicks(7000),
  mana_cost: 15,
  range: {
    type: SkillRangeType.POINT_TARGET,
    value: 4,
  },
  event_checks: [
    {
      conditions: [
        {
          type: "TARGET_HAS_STATUS",
          status: StatusEffect.ATTACK_UP,
        },
      ],
      effects: [
        {
          type: "COOLDOWN_RECHARGE",
          value: 0.5,
        },
      ],
    },
  ],
  on_hit: [],
  on_cast: [
    {
      __typename: "Vfx",
      duration_ticks: 25,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/vfx/spellcast__39.png",
      sprite_scale: 1.5,
      speed: 0.4,
      blocks_event_pipeline: true,
    } as SkillEvent_Vfx,
    {
      __typename: "ProjectileCast",
      sprite_path: "/assets/spells/projectile/ghost-1.png",
      duration_ticks: 35,
      damage: 10,
      speed: 40,
      sprite_scale: 1.3,
      target: { type: "enemy", id: 0 },
      status_effects: [],
      starting_tick: 0,
      blocks_event_pipeline: true,
    } as SkillEvent_ProjectileCast,
    {
      __typename: "StealBuff",
      duration_ticks: 1,
      target: { type: "enemy", id: 0 },
      status_effects: [],
      starting_tick: 0,
      steal_count: 1,
      blocks_event_pipeline: false,
    } as SkillEvent_StealBuff,
  ],
};

export const SNATCH: SkillData = {
  id: "snatch",
  name: "Snatch",
  description: "Steals ALL buffs from a single target.",
  power: 5,
  pokemon_type: PokemonType.DARK,
  types: [SkillType.DAMAGE],
  cooldown_ticks: msToTicks(6000),
  mana_cost: 25,
  range: {
    type: SkillRangeType.POINT_TARGET,
    value: 3,
  },
  on_hit: [],
  on_cast: [
    {
      __typename: "Vfx",
      duration_ticks: 30,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/tile-fx/dark-1.png",
      sprite_scale: 2,
      speed: 0.3,
      blocks_event_pipeline: true,
    } as SkillEvent_Vfx,
    {
      __typename: "MeleeHit",
      duration_ticks: 25,
      target: { type: "enemy", id: 0 },
      status_effects: [],
      starting_tick: 0,
      damage: 5,
      blocks_event_pipeline: true,
    } as SkillEvent_MeleeHit,
    {
      __typename: "StealBuff",
      duration_ticks: 1,
      target: { type: "enemy", id: 0 },
      status_effects: [],
      starting_tick: 0,
      steal_count: 0, // 0 = all buffs
      blocks_event_pipeline: false,
    } as SkillEvent_StealBuff,
  ],
};

export const LEECH_STEAL: SkillData = {
  id: "leech-steal",
  name: "Leech Steal",
  description:
    "Steals defense buff from target and heals self. Parasitic drain.",
  power: 8,
  pokemon_type: PokemonType.BUG,
  types: [SkillType.DAMAGE],
  cooldown_ticks: msToTicks(8000),
  mana_cost: 15,
  range: {
    type: SkillRangeType.POINT_TARGET,
    value: 3,
  },
  on_hit: [],
  on_cast: [
    // Wave 1: VFX on both + projectile
    {
      __typename: "Vfx",
      duration_ticks: 15,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/vfx/bugbuff__9.png",
      sprite_scale: 1.5,
      speed: 0.6,
      blocks_event_pipeline: false,
    } as SkillEvent_Vfx,
    {
      __typename: "Vfx",
      duration_ticks: 15,
      target: { type: "enemy", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/vfx/bugbuff__9.png",
      sprite_scale: 1.5,
      speed: 0.6,
      blocks_event_pipeline: false,
    } as SkillEvent_Vfx,
    {
      __typename: "ProjectileCast",
      sprite_path: "/assets/spells/projectile/string-shot.png",
      duration_ticks: 15,
      damage: 3,
      speed: 60,
      sprite_scale: 1.5,
      target: { type: "enemy", id: 0 },
      status_effects: [],
      starting_tick: 0,
      blocks_event_pipeline: true,
    } as SkillEvent_ProjectileCast,
    // Wave 2: VFX on both + projectile
    {
      __typename: "Vfx",
      duration_ticks: 15,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/vfx/bugbuff__9.png",
      sprite_scale: 1.5,
      speed: 0.6,
      blocks_event_pipeline: false,
    } as SkillEvent_Vfx,
    {
      __typename: "Vfx",
      duration_ticks: 15,
      target: { type: "enemy", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/vfx/bugbuff__9.png",
      sprite_scale: 1.5,
      speed: 0.6,
      blocks_event_pipeline: false,
    } as SkillEvent_Vfx,
    {
      __typename: "ProjectileCast",
      sprite_path: "/assets/spells/projectile/string-shot.png",
      duration_ticks: 15,
      damage: 3,
      speed: 60,
      sprite_scale: 1.5,
      target: { type: "enemy", id: 0 },
      status_effects: [],
      starting_tick: 0,
      blocks_event_pipeline: true,
    } as SkillEvent_ProjectileCast,
    // Wave 3: VFX on both + projectile
    {
      __typename: "Vfx",
      duration_ticks: 15,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/vfx/bugbuff__9.png",
      sprite_scale: 1.5,
      speed: 0.6,
      blocks_event_pipeline: false,
    } as SkillEvent_Vfx,
    {
      __typename: "Vfx",
      duration_ticks: 15,
      target: { type: "enemy", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/vfx/bugbuff__9.png",
      sprite_scale: 1.5,
      speed: 0.6,
      blocks_event_pipeline: false,
    } as SkillEvent_Vfx,
    {
      __typename: "ProjectileCast",
      sprite_path: "/assets/spells/projectile/string-shot.png",
      duration_ticks: 15,
      damage: 2,
      speed: 60,
      sprite_scale: 1.5,
      target: { type: "enemy", id: 0 },
      status_effects: [],
      starting_tick: 0,
      blocks_event_pipeline: true,
    } as SkillEvent_ProjectileCast,
    {
      __typename: "StealBuff",
      duration_ticks: 1,
      target: { type: "enemy", id: 0 },
      status_effects: [],
      starting_tick: 0,
      steal_count: 1,
      steal_types: [StatusEffect.DEFENSE_UP],
      heal_on_steal: 15,
      blocks_event_pipeline: false,
    } as SkillEvent_StealBuff,
  ],
};

export const POWER_SIPHON: SkillData = {
  id: "power-siphon",
  name: "Power Siphon",
  description: "Steals attack buff from target. Take their strength.",
  power: 12,
  pokemon_type: PokemonType.ROCK,
  types: [SkillType.DAMAGE],
  cooldown_ticks: msToTicks(8000),
  mana_cost: 15,
  range: {
    type: SkillRangeType.POINT_TARGET,
    value: 3,
  },
  on_hit: [],
  on_cast: [
    {
      __typename: "Vfx",
      duration_ticks: 25,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/vfx/rock-impact__11.png",
      sprite_scale: 1.5,
      speed: 0.4,
      blocks_event_pipeline: true,
    } as SkillEvent_Vfx,
    {
      __typename: "ProjectileCast",
      sprite_path: "/assets/spells/projectile/rock-throw.png",
      duration_ticks: 35,
      damage: 12,
      speed: 40,
      sprite_scale: 1.2,
      target: { type: "enemy", id: 0 },
      status_effects: [],
      starting_tick: 0,
      blocks_event_pipeline: true,
    } as SkillEvent_ProjectileCast,
    {
      __typename: "StealBuff",
      duration_ticks: 1,
      target: { type: "enemy", id: 0 },
      status_effects: [],
      starting_tick: 0,
      steal_count: 1,
      steal_types: [StatusEffect.ATTACK_UP],
      blocks_event_pipeline: false,
    } as SkillEvent_StealBuff,
  ],
};

// More unique skills
export const IRON_DEFENSE: SkillData = {
  id: "iron-defense",
  name: "Iron Defense",
  description: "Defend yourself with a shield of iron.",
  power: 0,
  pokemon_type: PokemonType.STEEL,
  types: [SkillType.BUFF],
  cooldown_ticks: msToTicks(5000),
  mana_cost: 10,
  range: {
    type: SkillRangeType.SELF,
    value: 3,
  },
  on_hit: [],
  on_cast: [
    {
      __typename: "Vfx",
      duration_ticks: 25,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/vfx/circlesaw__15.png",
      sprite_scale_x: 2.5,
      sprite_scale_y: 1.75,
      speed: 0.4,
      blocks_event_pipeline: true,
      loops: 3,
    } as SkillEvent_Vfx,
    {
      __typename: "Shield",
      duration_ticks: 25,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      shield_amount: 10,
      shield_duration_ticks: 25,
    } as SkillEvent_Shield,
  ],
  charging: {
    is_required: true,
    duration_ticks: 50,
    align_to: "south",
    anim_on_end: {
      name: "pose",
      dir: "south",
    },
  },
};

export const TRI_ATTACK: SkillData = {
  id: "tri-attack",
  name: "Tri Attack",
  description:
    "A ranged attack that shoots 3 projectiles in a row. Reduces cooldown by 10% per use, up to 50% reduction.",
  power: 10,
  pokemon_type: PokemonType.NORMAL,
  types: [SkillType.DAMAGE],
  cooldown_ticks: msToTicks(4500),
  mana_cost: 10,
  range: {
    type: SkillRangeType.POINT_TARGET,
    value: 3,
  },
  momentum: {
    max_stacks: 5,
    effects: [
      {
        type: "cooldown_mult",
        value_per_stack: 0.1,
      },
    ],
  } as SkillMomentum,
  on_hit: [
    {
      __typename: "Vfx",
      duration_ticks: 25,
      target: { type: "enemy", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path:
        "/assets/spells/impact/impfx2_fantasy_impact_A_large_red.png",
      sprite_scale: 1.5,
      speed: 0.6,
    } as SkillEvent_Vfx,
  ],
  on_cast: [
    {
      __typename: "ProjectileCast",
      target: { type: "enemy", id: 0 },
      sprite_path: "/assets/spells/projectile/triattack__4.png",
      status_effects: [],
      starting_tick: 0,
      duration_ticks: 11,
      damage: 3,
      speed: 35,
      sprite_scale: 2.5,
      blocks_event_pipeline: true,
    } as SkillEvent_ProjectileCast,
    {
      __typename: "ProjectileCast",
      target: { type: "enemy", id: 0 },
      sprite_path: "/assets/spells/projectile/triattack__4.png",
      status_effects: [],
      starting_tick: 0,
      duration_ticks: 11,
      damage: 4,
      speed: 35,
      sprite_scale: 2.5,
      blocks_event_pipeline: true,
    } as SkillEvent_ProjectileCast,
    {
      __typename: "ProjectileCast",
      target: { type: "enemy", id: 0 },
      sprite_path: "/assets/spells/projectile/triattack__4.png",
      status_effects: [],
      starting_tick: 0,
      duration_ticks: 15,
      damage: 5,
      speed: 35,
      sprite_scale: 2.5,
    } as SkillEvent_ProjectileCast,
  ],
};

export const BONE_THROW: SkillData = {
  id: "bone-throw",
  name: "Bone Throw",
  description: "Throws a bone. Signature Cubone/Marowak move.",
  power: 12,
  pokemon_type: PokemonType.GROUND,
  types: [SkillType.DAMAGE],
  cooldown_ticks: msToTicks(3000),
  mana_cost: 8,
  range: {
    type: SkillRangeType.POINT_TARGET,
    value: 5,
  },
  momentum: {
    max_stacks: 6,
    effects: [
      {
        type: "cooldown_mult",
        value_per_stack: 0.1,
      },
    ],
  } as SkillMomentum,
  on_hit: [
    {
      __typename: "Vfx",
      duration_ticks: 30,
      target: {
        type: "enemy",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/impact/bonemerang_001__6.png",
      sprite_scale: 1.5,
      speed: 0.5,
      blocks_event_pipeline: false,
    } as SkillEvent_Vfx,
  ],
  on_cast: [
    {
      __typename: "ProjectileCast",
      sprite_path: "/assets/spells/projectile/bonemerang_000__8.png",
      duration_ticks: 40,
      damage: 12,
      speed: 30,
      sprite_scale: 3,
      target: {
        type: "enemy",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
      blocks_event_pipeline: true,
    } as SkillEvent_ProjectileCast,
  ],
};

export const BONEMERANG: SkillData = {
  id: "bonemerang",
  name: "Bonemerang",
  description:
    "A ranged attack that shoots a bonemerange that goes forward and then goes back..",
  power: 20,
  pokemon_type: PokemonType.GROUND,
  types: [SkillType.DAMAGE],
  cooldown_ticks: msToTicks(4500),
  mana_cost: 10,
  range: {
    type: SkillRangeType.ANCHORED_INVOKE,
    value: 4,
  },
  cast_pattern: LINE_LOOP_PATTERN(5),
  align_to_user_dir: true,
  charging: {
    is_required: true,
    duration_ticks: 15,
    align_to: "target",
    anim_on_end: {
      name: "special",
      dir: "target",
    },
  },
  on_hit: [],
  on_cast: [
    ...LINE_LOOP_PATTERN(5).East.map(
      ([x, y]) =>
        ({
          __typename: "AnchoredInvoke",
          duration_ticks: 10,
          target: { type: "self", id: 0 },
          status_effects: [],
          starting_tick: 0,
          relative_x: x,
          relative_y: y,
          sprite_path: "/assets/spells/projectile/bonemerang_000__8.png",
          sprite_scale: 2.5,
          anim_speed: 3,
          blocks_event_pipeline: true,
          anim_loops: 2,
        }) as SkillEvent_AnchoredInvoke,
    ),
  ],
};

export const POISON_POWDER: SkillData = {
  id: "poison-powder",
  name: "Poison Powder",
  description:
    "Creates a cloud of poison powder around the user that poisons enemies on contact",
  power: 0,
  pokemon_type: PokemonType.POISON,
  types: [SkillType.DEBUFF],
  cooldown_ticks: msToTicks(5000),
  mana_cost: 10,
  range: {
    type: SkillRangeType.ANCHORED_INVOKE,
    value: 2,
  },
  charging: {
    is_required: true,
    duration_ticks: 15,
    align_to: "south",
    anim_on_end: {
      name: "pose",
      dir: "south",
    },
  },
  reactive_triggers: [
    {
      event: PokemonEventType.TAKE_DAMAGE,
      chance: 1,
      action: "cooldown_reset",
      filters: {
        target_is_self: true,
      },
    } as ReactiveTriggerConfig,
  ],
  on_hit: [],
  on_cast: [
    ...SQUARE_2_PATTERN().North.map(
      ([x, y]) =>
        ({
          __typename: "AnchoredInvoke",
          duration_ticks: 15,
          target: { type: "self", id: 0 },
          status_effects: [
            { status: StatusEffect.POISON, duration_ticks: 200, intensity: 2 },
          ],
          starting_tick: 0,
          relative_x: x,
          relative_y: y,
          sprite_path: "/assets/spells/tile-fx/powder_green_002__22.png",
          sprite_scale: 1.5,
          anim_speed: 0.65,
          anim_loops: 1,
        }) as SkillEvent_AnchoredInvoke,
    ),
  ],
};

export const STUN_SPORE: SkillData = {
  id: "stun-spore",
  name: "Stun Spore",
  description:
    "Creates a cloud of stun spores around the user that stuns enemies on contact",
  power: 0,
  pokemon_type: PokemonType.POISON,
  types: [SkillType.DEBUFF],
  cooldown_ticks: msToTicks(5000),
  mana_cost: 10,
  range: {
    type: SkillRangeType.ANCHORED_INVOKE,
    value: 2,
  },
  charging: {
    is_required: true,
    duration_ticks: 15,
    align_to: "south",
    anim_on_end: {
      name: "pose",
      dir: "south",
    },
  },
  on_hit: [],
  on_cast: [
    ...SQUARE_2_PATTERN().North.map(
      ([x, y]) =>
        ({
          __typename: "AnchoredInvoke",
          duration_ticks: 15,
          target: { type: "self", id: 0 },
          status_effects: [
            { status: StatusEffect.STUN, duration_ticks: 200, intensity: 1 },
          ],
          starting_tick: 0,
          relative_x: x,
          relative_y: y,
          sprite_path: "/assets/spells/tile-fx/powder_green_000__22.png",
          sprite_scale: 1.5,
          anim_speed: 0.65,
          anim_loops: 1,
        }) as SkillEvent_AnchoredInvoke,
    ),
  ],
};

// ====================================
// NEW MECHANIC SKILLS
// ====================================

// KINESIS - Channeling + Knockup skill (Psychic)
export const KINESIS: SkillData = {
  id: "kinesis",
  name: "Kinesis",
  description:
    "The user channels psychic power, suspending the target in the air",
  power: 10,
  pokemon_type: PokemonType.PSYCHIC,
  types: [SkillType.DEBUFF],
  cooldown_ticks: msToTicks(8000),
  mana_cost: 15,
  range: {
    type: SkillRangeType.POINT_TARGET,
    value: 4,
  },
  charging: {
    is_required: true,
    duration_ticks: msToTicks(2500),
    align_to: "target",
    anim_on_end: {
      name: "pose",
      dir: "target",
    },
    // Channeling options
    is_channel: true,
    interruptible: false,
    channel_status: {
      status: StatusEffect.KNOCKUP,
      duration_ticks: msToTicks(2500),
      intensity: 1,
    },
  },
  on_hit: [],
  on_cast: [
    {
      __typename: "Vfx",
      duration_ticks: 120,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/vfx/spoon__12.png",
      sprite_scale: 1.25,
      speed: 0.65,
      blocks_event_pipeline: false,
      loops: 3,
      anchor_to_target: true,
      y_offset: -80,
    } as SkillEvent_Vfx,
    {
      __typename: "Vfx",
      duration_ticks: 120,
      target: { type: "enemy", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/vfx/spellcast-b__39.png",
      sprite_scale: 1.25,
      speed: 0.65,
      blocks_event_pipeline: true,
      loops: 2,
      anchor_to_target: true,
      y_offset: -20,
      damage: 20,
    } as SkillEvent_Vfx,
  ],
};

// GUARDIAN BOND - Tether skill (Fairy - IO style)
export const GUARDIAN_BOND: SkillData = {
  id: "guardian-bond",
  name: "Guardian Bond",
  description: "Creates a healing link with an ally that heals over time",
  power: 0,
  pokemon_type: PokemonType.FAIRY,
  types: [SkillType.HEAL, SkillType.BUFF],
  cooldown_ticks: msToTicks(10000),
  mana_cost: 20,
  range: {
    type: SkillRangeType.ALL_ALLIES,
    value: 6,
  },
  on_hit: [],
  on_cast: [
    {
      __typename: "Tether",
      duration_ticks: 15000,
      target: { type: "ally", id: 0 },
      status_effects: [],
      starting_tick: 0,
      tether_type: "ally",
      max_distance: 5,
      tick_interval: 30,
      periodic_heal: 8,
      blocks_event_pipeline: false,
      tether_line_config: {
        color: 0x68e3db,
        width: 4,
        pulse: true,
        energy_flow: true,
        glow: true,
        enabled: true,
      },
    } as SkillEvent_Tether,
  ],
  event_checks: [
    {
      conditions: [
        {
          type: "TARGET_IS_TYPE",
          pokemon_type: PokemonType.FAIRY,
        },
      ],
      effects: [
        {
          type: "HEAL_ALL_ALLIES_FIXED",
          value: 10,
        },
      ],
    },
  ] as SkillData["event_checks"],
};

//  Pull/Hook skill (Pudge style)
export const SPECTRAL_CHAIN: SkillData = {
  id: "spectral-chain",
  name: "Spectral Chain",
  description: "Hooks an enemy and pulls them close, stunning them briefly",
  power: 20,
  pokemon_type: PokemonType.GHOST,
  types: [SkillType.DAMAGE, SkillType.DEBUFF],
  cooldown_ticks: msToTicks(10000),
  mana_cost: 18,
  range: {
    type: SkillRangeType.POINT_TARGET,
    value: 5,
  },
  on_hit: [],
  on_cast: [
    {
      __typename: "Pull",
      duration_ticks: 20,
      target: { type: "enemy", id: 0 },
      status_effects: [],
      starting_tick: 0,
      pull_scope: "single",
      stun_ticks: 60,
      damage: 15,
      blocks_event_pipeline: true,
    } as SkillEvent_Pull,
  ],
};

export const VINE_WHIP: SkillData = {
  id: "vine-whip",
  name: "Vine Whip",
  description: "Hooks an enemy and pulls them close, stunning them briefly",
  power: 20,
  pokemon_type: PokemonType.GRASS,
  types: [SkillType.DAMAGE, SkillType.DEBUFF],
  cooldown_ticks: msToTicks(10000),
  mana_cost: 18,
  range: {
    type: SkillRangeType.POINT_TARGET,
    value: 5,
  },
  on_hit: [],
  on_cast: [
    {
      __typename: "Pull",
      duration_ticks: 20,
      target: { type: "enemy", id: 0 },
      status_effects: [],
      starting_tick: 0,
      pull_scope: "single",
      stun_ticks: 60,
      damage: 15,
      blocks_event_pipeline: true,
      hook_line_config: {
        enabled: true,
        color: 0x26c459,
      },
    } as SkillEvent_Pull,
  ],
};

// Multi-target Pull skill (Dragon - Magnus RP style)
export const DRAGON_VORTEX: SkillData = {
  id: "dragon-vortex",
  name: "Dragon Vortex",
  description: "Creates a massive vortex that pulls all enemies to the caster",
  power: 15,
  pokemon_type: PokemonType.DRAGON,
  types: [SkillType.DAMAGE, SkillType.DEBUFF],
  cooldown_ticks: msToTicks(15000),
  mana_cost: 35,
  range: {
    type: SkillRangeType.ALL_ENEMIES,
    value: 0,
  },

  team_conditions: [
    {
      condition: {
        type: "min_ally_type_count",
        pokemon_type: PokemonType.DARK,
        value: 1,
      },
      effect: {
        stat: "cooldown_recharge",
        value: 0.5,
      } as TeamConditionEffect,
    },
  ],

  charging: {
    is_required: true,
    duration_ticks: 80,
    align_to: "south",
    anim_on_end: {
      name: "special",
      dir: "south",
    },
  },
  on_hit: [],
  on_cast: [
    {
      __typename: "Vfx",
      duration_ticks: 80,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/vfx/charge__14.png",
      sprite_scale: 1,
      sprite_alpha: 1,
      speed: 0.5,
      anchor_to_target: true,
    } as SkillEvent_Vfx,
    // Expanding wave using AnchoredInvoke - Ring 1 (immediate)
    // Ring 3 (most delayed by second wait)
    ...(
      [
        [0, -3],
        [0, 3],
        [-3, 0],
        [3, 0],
        [-1, -2],
        [-1, 2],
        [1, -2],
        [1, 2],
        [-2, -1],
        [-2, 1],
        [2, -1],
        [2, 1],
      ] as [number, number][]
    ).map(
      ([x, y]) =>
        ({
          __typename: "AnchoredInvoke",
          duration_ticks: 10,
          target: { type: "self", id: 0 },
          status_effects: [],
          starting_tick: 0,
          relative_x: x,
          relative_y: y,
          sprite_path: "/assets/spells/vfx/spheresbuff__21.png",
          sprite_scale_x: 1.4,
          sprite_scale_y: 0.9,
          anim_speed: 1.5,
          damage: 0,
          blocks_event_pipeline: false,
        }) as SkillEvent_AnchoredInvoke,
    ),
    {
      __typename: "Wait",
      duration_ticks: 10,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      blocks_event_pipeline: true,
    } as SkillEvent_Wait,

    // PULL ALL ENEMIES
    {
      __typename: "Pull",
      duration_ticks: 10,
      target: { type: "enemy", id: 0 },
      status_effects: [],
      starting_tick: 0,
      pull_scope: "all_enemies",
      stun_ticks: 80,
      damage: 25,
      blocks_event_pipeline: false,
      hook_line_config: {
        enabled: false,
      },
    } as SkillEvent_Pull,

    // Ring 2 (delayed by wait)
    ...(
      [
        [0, -2],
        [0, 2],
        [-2, 0],
        [2, 0],
        [-1, -1],
        [-1, 1],
        [1, -1],
        [1, 1],
      ] as [number, number][]
    ).map(
      ([x, y]) =>
        ({
          __typename: "AnchoredInvoke",
          duration_ticks: 10,
          target: { type: "self", id: 0 },
          status_effects: [],
          starting_tick: 0,
          relative_x: x,
          relative_y: y,
          sprite_path: "/assets/spells/vfx/spheresbuff__21.png",
          sprite_scale_x: 1.4,
          sprite_scale_y: 0.9,
          anim_speed: 1.5,
          damage: 0,
          blocks_event_pipeline: false,
        }) as SkillEvent_AnchoredInvoke,
    ),
    {
      __typename: "Wait",
      duration_ticks: 10,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      blocks_event_pipeline: true,
    } as SkillEvent_Wait,

    ...(
      [
        [0, -1],
        [0, 1],
        [-1, 0],
        [1, 0],
      ] as [number, number][]
    ).map(
      ([x, y]) =>
        ({
          __typename: "AnchoredInvoke",
          duration_ticks: 15,
          target: { type: "self", id: 0 },
          status_effects: [],
          starting_tick: 0,
          relative_x: x,
          relative_y: y,
          sprite_path: "/assets/spells/vfx/spheresbuff__21.png",
          sprite_scale_x: 1.4,
          sprite_scale_y: 0.9,
          anim_speed: 1.5,
          damage: 0,
          blocks_event_pipeline: false,
        }) as SkillEvent_AnchoredInvoke,
    ),
  ],
};

export const MAGNET_PULL: SkillData = {
  id: "magnet-pull",
  name: "Magnet Pull",
  description: "Creates a magnetic pull that pulls all enemies to the caster",
  power: 15,
  pokemon_type: PokemonType.STEEL,
  types: [SkillType.DAMAGE, SkillType.DEBUFF],
  cooldown_ticks: msToTicks(14000),
  mana_cost: 35,
  range: {
    type: SkillRangeType.ALL_ENEMIES,
    value: 0,
  },
  charging: {
    is_required: true,
    duration_ticks: 80,
    align_to: "south",
    anim_on_end: {
      name: "special",
      dir: "south",
    },
  },
  team_conditions: [
    {
      condition: {
        type: "min_ally_type_count",
        pokemon_type: PokemonType.ELECTRIC,
        value: 1,
      },
      effect: {
        stat: "apply_status",
        value: 1,
        status_effects: [
          {
            effect: {
              status: StatusEffect.STUN,
              duration_ticks: msToTicks(2000),
              intensity: 1,
            },
            target: "all_enemies",
          },
        ],
      } as TeamConditionEffect,
    },
  ] as SkillData["team_conditions"],
  on_hit: [],
  on_cast: [
    {
      __typename: "Vfx",
      duration_ticks: 80,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/vfx/whiteredbuff__21.png",
      sprite_scale: 1,
      sprite_alpha: 1,
      speed: 0.5,
      anchor_to_target: true,
    } as SkillEvent_Vfx,
    // Expanding wave using AnchoredInvoke - Ring 1 (immediate)
    // Ring 3 (most delayed by second wait)
    ...(
      [
        [0, -3],
        [0, 3],
        [-3, 0],
        [3, 0],
        [-1, -2],
        [-1, 2],
        [1, -2],
        [1, 2],
        [-2, -1],
        [-2, 1],
        [2, -1],
        [2, 1],
      ] as [number, number][]
    ).map(
      ([x, y]) =>
        ({
          __typename: "AnchoredInvoke",
          duration_ticks: 10,
          target: { type: "self", id: 0 },
          status_effects: [],
          starting_tick: 0,
          relative_x: x,
          relative_y: y,
          sprite_path: "/assets/spells/vfx/circlesaw__15.png",
          sprite_scale_x: 1.4,
          sprite_scale_y: 0.9,
          anim_speed: 1.5,
          damage: 0,
          blocks_event_pipeline: false,
        }) as SkillEvent_AnchoredInvoke,
    ),
    {
      __typename: "Wait",
      duration_ticks: 10,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      blocks_event_pipeline: true,
    } as SkillEvent_Wait,

    // PULL ALL ENEMIES
    {
      __typename: "Pull",
      duration_ticks: 10,
      target: { type: "enemy", id: 0 },
      status_effects: [],
      starting_tick: 0,
      pull_scope: "all_enemies",
      stun_ticks: 80,
      damage: 25,
      blocks_event_pipeline: false,
      hook_line_config: {
        enabled: false,
      },
    } as SkillEvent_Pull,

    // Ring 2 (delayed by wait)
    ...(
      [
        [0, -2],
        [0, 2],
        [-2, 0],
        [2, 0],
        [-1, -1],
        [-1, 1],
        [1, -1],
        [1, 1],
      ] as [number, number][]
    ).map(
      ([x, y]) =>
        ({
          __typename: "AnchoredInvoke",
          duration_ticks: 10,
          target: { type: "self", id: 0 },
          status_effects: [],
          starting_tick: 0,
          relative_x: x,
          relative_y: y,
          sprite_path: "/assets/spells/vfx/circlesaw__15.png",
          sprite_scale_x: 1.4,
          sprite_scale_y: 0.9,
          anim_speed: 1.5,
          damage: 0,
          blocks_event_pipeline: false,
        }) as SkillEvent_AnchoredInvoke,
    ),
    {
      __typename: "Wait",
      duration_ticks: 10,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      blocks_event_pipeline: true,
    } as SkillEvent_Wait,

    ...(
      [
        [0, -1],
        [0, 1],
        [-1, 0],
        [1, 0],
      ] as [number, number][]
    ).map(
      ([x, y]) =>
        ({
          __typename: "AnchoredInvoke",
          duration_ticks: 15,
          target: { type: "self", id: 0 },
          status_effects: [],
          starting_tick: 0,
          relative_x: x,
          relative_y: y,
          sprite_path: "/assets/spells/vfx/circlesaw__15.png",
          sprite_scale_x: 1.4,
          sprite_scale_y: 0.9,
          anim_speed: 1.5,
          damage: 0,
          blocks_event_pipeline: false,
        }) as SkillEvent_AnchoredInvoke,
    ),
  ],
};

export const POWER_WHIP: SkillData = {
  id: "power-whip",
  name: "Power Whip",
  description: "Shoots powerful whips that pulls enemies and deals stun",
  power: 15,
  pokemon_type: PokemonType.GRASS,
  types: [SkillType.DAMAGE, SkillType.DEBUFF],
  cooldown_ticks: msToTicks(14000),
  mana_cost: 35,
  range: {
    type: SkillRangeType.ALL_ENEMIES,
    value: 0,
  },
  charging: {
    is_required: true,
    duration_ticks: 80,
    align_to: "south",
    anim_on_end: {
      name: "special",
      dir: "south",
    },
  },
  on_hit: [],
  on_cast: [
    {
      __typename: "Vfx",
      duration_ticks: 80,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/self/self-buff-green_17.png",
      sprite_scale: 1,
      sprite_alpha: 1,
      speed: 0.5,
      anchor_to_target: true,
    } as SkillEvent_Vfx,

    // PULL ALL ENEMIES
    {
      __typename: "Pull",
      duration_ticks: 10,
      target: { type: "enemy", id: 0 },
      status_effects: [],
      starting_tick: 0,
      pull_scope: "all_enemies",
      stun_ticks: 80,
      damage: 25,
      blocks_event_pipeline: false,
      hook_line_config: {
        enabled: true,
        color: 0x26c459,
        width: 10,
      },
    } as SkillEvent_Pull,
  ],
};

// QUICK TEMPO - CooldownSkip skill (Electric)
export const QUICK_TEMPO: SkillData = {
  id: "quick-tempo",
  name: "Quick Tempo",
  description: "Energizes all allies, reducing their skill cooldowns",
  power: 0,
  pokemon_type: PokemonType.ELECTRIC,
  types: [SkillType.BUFF],
  cooldown_ticks: msToTicks(7500),
  mana_cost: 25,
  range: {
    type: SkillRangeType.ALL_ALLIES,
    value: 0,
  },
  on_hit: [],
  on_cast: [
    {
      __typename: "CooldownSkip",
      duration_ticks: 30,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      skip_ticks: msToTicks(1000),
      target_scope: "all_allies",
      blocks_event_pipeline: false,
    } as SkillEvent_CooldownSkip,

    {
      __typename: "Global",
      duration_ticks: 30,
      target: { type: "ally", id: 0 },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/vfx/self_sparkles_000__48.png",
      sprite_scale: 1,
      speed: 0.5,
      anchor_to_target: true,
      blocks_event_pipeline: false,
    } as SkillEvent_Global,
  ],
};

export const SYNCHRONIZE: SkillData = {
  id: "synchronize",
  starts_active: false,
  name: "Synchronize",
  description: "Synchronizes with an ally, reducing their skill cooldowns",
  power: 0,
  pokemon_type: PokemonType.PSYCHIC,
  types: [SkillType.BUFF],
  cooldown_ticks: msToTicks(6000),
  mana_cost: 25,
  range: {
    type: SkillRangeType.ALL_ALLIES,
    value: 0,
  },
  on_hit: [],
  on_cast: [
    {
      __typename: "CooldownSkip",
      duration_ticks: 30,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      skip_ticks: msToTicks(2500),
      target_scope: "ally",
      blocks_event_pipeline: false,
    } as SkillEvent_CooldownSkip,
  ],
};

export const GEAR_UP: SkillData = {
  id: "gear-up",
  name: "Gear Up",
  starts_active: false,
  description: "Reduce the cooldowns of the user and raises speed.",
  power: 0,
  pokemon_type: PokemonType.STEEL,
  types: [SkillType.BUFF],
  cooldown_ticks: msToTicks(10000),
  mana_cost: 25,
  range: {
    type: SkillRangeType.SELF,
    value: 0,
  },
  team_conditions: [
    {
      condition: {
        type: "min_ally_type_count",
        pokemon_type: PokemonType.STEEL,
        value: 1,
      },
      effect: {
        stat: "apply_status",
        value: 1,
        status_effects: [
          {
            effect: {
              status: StatusEffect.SPEED_UP,
              duration_ticks: msToTicks(3000),
              intensity: 1,
            },
            target: "all_allies",
          },
        ],
      } as TeamConditionEffect,
    },
  ] as SkillData["team_conditions"],
  on_hit: [],
  on_cast: [
    {
      __typename: "CooldownSkip",
      duration_ticks: 30,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      skip_ticks: msToTicks(2500),
      target_scope: "self",
      blocks_event_pipeline: false,
    } as SkillEvent_CooldownSkip,
    {
      __typename: "Vfx",
      duration_ticks: 30,
      target: { type: "self", id: 0 },
      status_effects: [
        {
          status: StatusEffect.SPEED_UP,
          duration_ticks: 300,
          intensity: 1,
        },
      ],
      starting_tick: 0,
      sprite_path: "/assets/spells/self/techaura__24.png",
      sprite_scale: 1,
      speed: 0.75,
      loops: 3,
      anchor_to_target: true,
    } as SkillEvent_Vfx,
  ],
};

export const AGILITY: SkillData = {
  id: "agility",
  name: "Agility",
  description: "Sharply raises the user's speed for 4 seconds.",
  power: 0,
  pokemon_type: PokemonType.PSYCHIC,
  types: [SkillType.BUFF],
  cooldown_ticks: msToTicks(6000),
  mana_cost: 20,
  range: {
    type: SkillRangeType.SELF,
    value: 0,
  },
  event_checks: [
    {
      conditions: [
        {
          type: "CASTER_HP_LT",
          value: 0.5,
        },
      ],
      effects: [
        {
          type: "CLEANSE_SELF",
          cleanse_config: {
            count: 0,
            specific_statuses: [],
          },
        },
      ],
    },
  ] as SkillData["event_checks"],
  on_hit: [],
  on_cast: [
    {
      __typename: "Vfx",
      duration_ticks: 30,
      target: { type: "self", id: 0 },
      status_effects: [
        {
          status: StatusEffect.SPEED_UP,
          duration_ticks: msToTicks(4000),
          intensity: 3,
        },
      ],
      starting_tick: 0,
      sprite_path: "/assets/spells/self/powerup-1.png",
      sprite_scale: 1.2,
      speed: 1,
      loops: 1,
      anchor_to_target: true,
    } as SkillEvent_Vfx,
  ],
};

export const FLOWER_RUSH: SkillData = {
  id: "flower-rush",
  name: "Flower Rush",
  description:
    "Shoots flowers all around the caster, dealing damage to enemies in the area. Reduces 10% cooldown per use, up to 50% reduction.",
  power: 10,
  pokemon_type: PokemonType.GRASS,
  types: [SkillType.DAMAGE],
  cooldown_ticks: msToTicks(7000),

  mana_cost: 15,
  momentum: {
    max_stacks: 5,
    effects: [
      {
        type: "cooldown_mult",
        value_per_stack: 0.1,
      },
    ],
  } as SkillMomentum,
  range: {
    type: SkillRangeType.ANCHORED_INVOKE,
    value: 3,
  },
  cast_pattern: SQUARE_2_PATTERN(),
  align_to_user_dir: false,
  charging: {
    is_required: true,
    align_to: "target",
    anim_on_end: {
      name: "rotate",
      dir: "south",
    },
    duration_ticks: 60,
  },
  on_hit: [
    {
      __typename: "Vfx",
      duration_ticks: 35,
      target: {
        type: "enemy",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/impact/tiny-pink-1.png",
      blocks_event_pipeline: false,
    } as SkillEvent_Vfx,
  ],
  on_cast: [
    ...Array.from({ length: 3 })
      .map((_, index) => [
        ...SQUARE_2_PATTERN().East.map(
          ([x, y, blocks]) =>
            ({
              __typename: "AnchoredInvoke",
              duration_ticks: 15,
              target: {
                type: "enemy",
                id: 0,
              },
              status_effects: [],
              starting_tick: 0,
              relative_x: x,
              relative_y: y,
              blocks_event_pipeline: false,
              damage: 10,
            }) as SkillEvent_AnchoredInvoke,
        ),
        {
          __typename: "ParticleEmission",
          duration_ticks: 10,
          target: {
            type: "enemy",
            id: 0,
          },
          status_effects: [],
          starting_tick: 0,
          particle_count: 40,
          particle_speed: 200,
          max_spawn_delay_ms: 150,
          pattern: SQUARE_2_PATTERN(),
          sprite_path: "/assets/spells/particle/flowers_000__26.png",
          sprite_scale: 2.2,
          anim_loops: 2,
          anim_speed: 0.5,
          should_align_pattern_to_user_dir: false,
          should_align_to_destination: true,
        } as SkillEvent_ParticleEmission,
        {
          __typename: "Wait",
          duration_ticks: 10,
          target: {
            type: "self",
            id: 0,
          },
          status_effects: [],
          starting_tick: 0,
          blocks_event_pipeline: true,
        } as SkillEvent_Wait,
      ])
      .flat(),
  ],
};

export const DRAIN_PUNCH: SkillData = {
  id: "drain-punch",
  name: "Drain Punch",
  description: "Deals damage to the target and restores the user's HP.",
  power: 10,
  pokemon_type: PokemonType.FIGHTING,
  types: [SkillType.DAMAGE],
  cooldown_ticks: msToTicks(4500),
  mana_cost: 15,
  range: {
    type: SkillRangeType.POINT_TARGET,
    value: 1,
  },
  on_hit: [
    {
      __typename: "Vfx",
      duration_ticks: 35,
      target: {
        type: "enemy",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/impact/tiny-white-1.png",
      blocks_event_pipeline: false,
    } as SkillEvent_Vfx,
  ],
  on_cast: [
    {
      __typename: "MeleeHit",
      duration_ticks: 35,
      target: {
        type: "enemy",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
      damage: 15,
    } as SkillEvent_MeleeHit,
    {
      __typename: "Heal",
      duration_ticks: 35,
      target: { type: "self", id: 0 },
      status_effects: [],
      starting_tick: 0,
      blocks_event_pipeline: false,
      heal_amount: 15,
    } as SkillEvent_Heal,
  ],
};

export const BOULDER_BASH: SkillData = {
  id: "boulder_bash",
  name: "Boulder Bash",
  description: "Jumps on the nearest target, apply stun to target and self",
  power: 10,
  pokemon_type: PokemonType.ROCK,
  types: [SkillType.DAMAGE],
  cooldown_ticks: msToTicks(3000),
  mana_cost: 15,
  range: {
    type: SkillRangeType.POINT_TARGET,
    value: 0,
  },
  on_hit: [
    {
      __typename: "Vfx",
      duration_ticks: 35,
      target: {
        type: "enemy",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/impact/melee-rock-1.png",
      sprite_scale: 1.25,
      speed: 0.5,
      blocks_event_pipeline: false,
    } as SkillEvent_Vfx,
  ],
  on_cast: [
    {
      __typename: "GapClose",
      duration_ticks: 35,
      target: {
        type: "enemy",
        id: 0,
      },
      status_effects: [
        {
          status: StatusEffect.STUN,
          duration_ticks: 320,
          intensity: 1,
        },
      ],
      starting_tick: 0,
      damage: 15,
      self_status_effects: [
        {
          status: StatusEffect.STUN,
          duration_ticks: 320,
          intensity: 1,
        },
      ],
      range: 0,
      gap_close_type: GapCloseType.TARGET_NEAREST,
    } as SkillEvent_GapClose,
  ],
};

export const ROCK_TOMB: SkillData = {
  id: "rock-tomb",
  name: "Rock Tomb",
  description:
    "Creates a rock barrier around the caster, becoming immovable and receiving a great shield.",
  power: 10,
  pokemon_type: PokemonType.ROCK,
  types: [SkillType.DAMAGE],
  cooldown_ticks: msToTicks(9000),
  mana_cost: 15,
  range: {
    type: SkillRangeType.SELF,
    value: 999,
  },
  team_conditions: [
    {
      condition: {
        type: "min_ally_type_count",
        pokemon_type: PokemonType.GROUND,
        value: 1,
      },
      effect: {
        stat: "shield_mult",
        value: 1.4,
      } as TeamConditionEffect,
    } as SkillTeamCondition,
  ],
  charging: {
    is_required: true,
    align_to: "target",
    anim_on_end: {
      name: "rotate",
      dir: "south",
    },
    duration_ticks: msToTicks(2500),
    is_channel: true,
    interruptible: false,
  },
  on_hit: [],
  on_cast: [
    {
      __typename: "Shield",
      duration_ticks: 25,
      target: {
        type: "self",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
      shield_amount: 99999,
      shield_duration_ticks: msToTicks(2500),
      blocks_event_pipeline: false,
    } as SkillEvent_Shield,
    {
      __typename: "Vfx",
      duration_ticks: 25,
      target: {
        type: "self",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/tile-fx/rock-2.png",
      sprite_scale: 1.25,
      speed: 0.25,
      loops: 4,
      anchor_to_target: true,
      blocks_event_pipeline: false,
    } as SkillEvent_Vfx,
    {
      __typename: "Wait",
      duration_ticks: msToTicks(2500),
      target: {
        type: "self",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
      blocks_event_pipeline: true,
    } as SkillEvent_Wait,
  ],
};

export const CHILLY_RECEPTION: SkillData = {
  id: "chilly-reception",
  name: "Chilly Reception",
  description: "Conects to an enemy and applies slow and damage while thetered",
  power: 0,
  pokemon_type: PokemonType.ICE,
  types: [SkillType.DAMAGE],
  cooldown_ticks: msToTicks(2000),
  mana_cost: 20,
  range: {
    type: SkillRangeType.ALL_ENEMIES,
    value: 5,
  },
  on_hit: [],
  on_cast: [
    {
      __typename: "Tether",
      duration_ticks: 2000,
      target: { type: "enemy", id: 0 },
      status_effects: [],
      starting_tick: 0,
      tether_type: "enemy",
      max_distance: 7,
      tick_interval: 30,
      target_effects: {
        periodicDamage: 5,
        periodicStatus: {
          status: StatusEffect.SPEED_DOWN,
          duration_ticks: 50,
          intensity: 1,
        },
      },
      blocks_event_pipeline: false,
      tether_line_config: {
        color: 0x68e3db,
        width: 8,
        pulse: true,
        energy_flow: true,
        glow: true,
        enabled: true,
      },
    } as SkillEvent_Tether,
  ],
};

export const SEISMIC_TOSS: SkillData = {
  id: "seismic-toss",
  name: "Seismic Toss",
  description: "Throws the target into the air, dealing damage.",
  power: 10,
  mana_cost: 15,
  pokemon_type: PokemonType.FIGHTING,
  types: [SkillType.DAMAGE],
  cooldown_ticks: msToTicks(3500),
  range: {
    type: SkillRangeType.POINT_TARGET,
    value: 1,
  },
  on_hit: [
    {
      __typename: "Vfx",
      duration_ticks: 35,
      target: {
        type: "enemy",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/impact/melee-1-red.png",
      sprite_scale: 1.33,
      speed: 0.35,
      blocks_event_pipeline: false,
      damage: 15,
    } as SkillEvent_Vfx,
  ],
  on_cast: [
    {
      __typename: "MeleeHit",
      duration_ticks: 35,
      target: {
        type: "enemy",
        id: 0,
      },
      status_effects: [
        {
          status: StatusEffect.KNOCKUP,
          duration_ticks: 50,
          intensity: 2,
        },
      ],
      starting_tick: 0,
      damage: 15,
    } as SkillEvent_MeleeHit,
  ],
};

export const INNER_SPIRIT: SkillData = {
  id: "inner-spirit",
  name: "Inner Spirit",
  description: "While charging, increases defense and heals user.",
  power: 0,
  pokemon_type: PokemonType.DRAGON,
  types: [SkillType.HEAL, SkillType.BUFF],
  cooldown_ticks: msToTicks(8000),
  mana_cost: 15,
  range: {
    type: SkillRangeType.SELF,
    value: 999,
  },
  charging: {
    is_required: true,
    align_to: "south",
    anim_on_end: {
      name: "pose",
      dir: "south",
    },
    duration_ticks: msToTicks(2500),
    is_channel: true,
    interruptible: false,
  },
  on_hit: [],
  on_cast: [
    {
      __typename: "Vfx",
      duration_ticks: 25,
      target: { type: "self", id: 0 },
      status_effects: [
        {
          status: StatusEffect.DEFENSE_UP,
          duration_ticks: msToTicks(5000),
          intensity: 3,
        },
      ],
      starting_tick: 0,
      sprite_path: "/assets/spells/self/shield-1-blue.png",
      sprite_scale: 1.25,
      speed: 0.22,
      loops: 5,
      blocks_event_pipeline: false,
    } as SkillEvent_Vfx,
    ...new Array(4).fill(0).map(
      (_, index) =>
        ({
          __typename: "Heal",
          duration_ticks: 30,
          target: { type: "self", id: 0 },
          status_effects: [],
          starting_tick: 0,
          blocks_event_pipeline: true,
          heal_amount: 10,
        }) as SkillEvent_Heal,
    ),
  ],
};